import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  usePublicShareOverview,
  usePublicShareBoard,
  usePublicShareAnalytics,
  usePublicShareTeam,
  useVerifySharePassword,
} from '../../hooks/api';
import {
  Lock, Eye, CheckCircle, Clock, AlertTriangle,
  Kanban, Calendar, Briefcase, Users, ListTodo,
  Building, Flag, List, LayoutGrid, Activity,
} from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title,
} from 'chart.js';
import { format } from 'date-fns';
import { toUTCPlus5 } from '../../utils/time';

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const SHARE_PASSWORD_KEY = 'share_password_';

const STATUS_ORDER = ['To Do', 'In Progress', 'In QA', 'Blocked', 'Done', 'Completed'];

const statusDot = {
  'To Do': 'bg-secondary-400',
  'In Progress': 'bg-primary-500',
  'In QA': 'bg-amber-400',
  'Blocked': 'bg-red-500',
  'Done': 'bg-emerald-500',
  'Completed': 'bg-emerald-500',
};

const priorityBadge = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const roleLabels = { admin: 'Admin', project_lead: 'Project Lead', member: 'Member' };

function getInitials(name) {
  return (name || '').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

function formatDue(d) {
  if (!d) return null;
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return null; }
}

function isOverdue(dueDate, status) {
  if (!dueDate || ['Done', 'Completed'].includes(status)) return false;
  return new Date(dueDate) < new Date();
}

export default function PublicProjectView() {
  const { token } = useParams();
  const [password, setPasswordState] = useState(() => {
    try { return sessionStorage.getItem(SHARE_PASSWORD_KEY + token) || ''; } catch { return ''; }
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [verified, setVerified] = useState(!!password);
  const [taskView, setTaskView] = useState('board');
  const [activeSection, setActiveSection] = useState('overview');
  const verifyPasswordMutation = useVerifySharePassword();

  const { data: overview, isLoading: overviewLoading, error: overviewError } = usePublicShareOverview(token, password, true);
  const hasData = overview && !overview.requires_password;
  const requiresPassword = overview?.requires_password === true;

  const { data: boardData } = usePublicShareBoard(token, password, hasData);
  const { data: analyticsData } = usePublicShareAnalytics(token, password, hasData);
  const { data: teamData } = usePublicShareTeam(token, password, hasData);

  const setPassword = useCallback((pwd) => {
    setPasswordState(pwd);
    try {
      if (pwd) sessionStorage.setItem(SHARE_PASSWORD_KEY + token, pwd);
      else sessionStorage.removeItem(SHARE_PASSWORD_KEY + token);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (overviewError?.response?.status === 401) {
      setPasswordState('');
      setVerified(false);
      try { sessionStorage.removeItem(SHARE_PASSWORD_KEY + token); } catch { /* ignore */ }
    }
  }, [overviewError?.response?.status, token]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const pwd = (passwordInput || '').trim();
    if (!pwd) { toast.error('Please enter the password'); return; }
    try {
      await verifyPasswordMutation.mutateAsync({ token, password: pwd });
      setPassword(pwd);
      setVerified(true);
      setPasswordInput('');
      toast.success('Access granted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid password');
    }
  };

  const columns = boardData?.columns || [];
  const allTasks = useMemo(() => columns.flatMap((c) => (c.tasks || []).map((t) => ({ ...t, status: t.status || c.status }))), [columns]);
  const members = teamData?.members || [];
  const sprints = overview?.sprints || [];
  const activity = overview?.recent_activity || [];

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (overviewLoading && !overview) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (overviewError?.response?.status === 404) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center p-6">
        <div className="card max-w-md text-center p-8">
          <p className="text-secondary-800 font-medium">This link is no longer available.</p>
          <p className="text-secondary-500 text-sm mt-2">The share link may have been removed or deactivated.</p>
        </div>
      </div>
    );
  }

  if (overviewError?.response?.status === 410) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center p-6">
        <div className="card max-w-md text-center p-8">
          <p className="text-secondary-800 font-medium">This share link has expired.</p>
        </div>
      </div>
    );
  }

  if (overviewError?.response?.status === 401) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center p-6">
        <PasswordForm
          title="Password required"
          subtitle={overviewError?.response?.data?.detail || 'Enter the password to view this project.'}
          subtitleColor="text-red-600"
          passwordInput={passwordInput} setPasswordInput={setPasswordInput}
          onSubmit={handlePasswordSubmit} loading={verifyPasswordMutation.isLoading}
        />
      </div>
    );
  }

  if (overviewError) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center p-6">
        <div className="card max-w-md text-center p-8">
          <p className="text-secondary-800 font-medium">Something went wrong.</p>
          <p className="text-secondary-500 text-sm mt-2">Please try again later.</p>
        </div>
      </div>
    );
  }

  if (requiresPassword && !verified) {
    return (
      <div className="public-share min-h-screen bg-secondary-50 flex items-center justify-center p-6">
        <PasswordForm
          title="Password required"
          subtitle={overview?.share_title}
          passwordInput={passwordInput} setPasswordInput={setPasswordInput}
          onSubmit={handlePasswordSubmit} loading={verifyPasswordMutation.isLoading}
        />
      </div>
    );
  }

  const pct = overview?.completion_percentage ?? 0;
  const total = overview?.tasks_count ?? 0;
  const done = overview?.completed_tasks_count ?? 0;
  const inProg = overview?.in_progress_tasks_count ?? 0;
  const todoCount = overview?.todo_tasks_count ?? 0;
  const inQa = overview?.in_qa_tasks_count ?? 0;
  const blocked = overview?.blocked_tasks_count ?? 0;
  const overdueCount = overview?.overdue_tasks_count ?? 0;
  const teamLoad = overview?.team_utilization_pct ?? 0;
  const estHours = analyticsData?.estimated_hours_total ?? 0;
  const actualHours = analyticsData?.actual_hours_total;
  const hoursPerWeek = analyticsData?.hours_per_week || [];
  const hoursByMember = analyticsData?.hours_by_member || [];
  const byStatus = analyticsData?.tasks_by_status?.length
    ? analyticsData.tasks_by_status
    : columns.map((c) => ({ status: c.status, count: (c.tasks || []).length }));

  const barData = hoursPerWeek.length ? {
    labels: hoursPerWeek.map((w) => {
      try { return format(new Date(w.week_start), 'MMM d'); } catch { return ''; }
    }),
    datasets: [{
      label: 'Hours logged',
      data: hoursPerWeek.map((w) => w.hours),
      backgroundColor: 'rgba(37, 99, 235, 0.75)',
      borderRadius: 4,
      borderSkipped: false,
    }],
  } : null;

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'sprints', label: 'Sprints' },
    { id: 'team', label: 'Team' },
    { id: 'time', label: 'Time' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className="public-share min-h-screen ps-muted text-secondary-900 overflow-x-hidden">
      {/* Header */}
      <header className="ps-surface border-b border-secondary-200">
        <div className="w-full min-w-0 px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-secondary-900 break-words">
                    {overview?.project_name || overview?.share_title || 'Project'}
                  </h1>
                  {overview?.project_status_label && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {overview.project_status_label}
                    </span>
                  )}
                </div>
                {overview?.project_description && (
                  <p className="text-sm text-secondary-600 leading-relaxed">{overview.project_description}</p>
                )}
            </div>
            <div className="flex-shrink-0 md:text-right">
              <div className="text-2xl sm:text-3xl font-bold text-primary-600 tabular-nums">{pct}%</div>
              <div className="text-xs text-secondary-500">complete</div>
            </div>
          </div>

          {/* Meta row */}
          <div className="mt-4 sm:mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            {overview?.client && (
              <MetaItem icon={Building} label="Client" value={overview.client} />
            )}
            {overview?.created_at && (
              <MetaItem icon={Calendar} label="Created" value={toUTCPlus5(overview.created_at)} />
            )}
            {overview?.creator_name && (
              <MetaItem icon={Users} label="Created by" value={overview.creator_name} />
            )}
            <MetaItem icon={Users} label="Team" value={`${overview?.member_count ?? members.length} members`} />
            <MetaItem icon={Kanban} label="Tasks" value={`${done}/${total} done`} />
          </div>
        </div>

        {/* Sticky nav */}
        <nav className="ps-nav border-t border-secondary-100 backdrop-blur-sm sticky top-0 z-30 public-share-nav">
          <div className="w-full min-w-0 px-3 sm:px-4 md:px-6 lg:px-8 flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className={`flex-shrink-0 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === item.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main className="w-full min-w-0 px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 space-y-8 sm:space-y-10">

        {/* ── Overview ── */}
        <section id="overview" className="scroll-mt-24 sm:scroll-mt-28">
          <SectionTitle title="Project progress" />

          <div className="card p-5 mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-secondary-600">{done} of {total} tasks completed</span>
              <span className="text-sm font-semibold text-primary-600">{pct}%</span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3 mb-5">
            <StatBox label="Total" value={total} />
            <StatBox label="Done" value={done} accent="text-emerald-600" />
            <StatBox label="In progress" value={inProg} accent="text-primary-600" />
            <StatBox label="To do" value={todoCount} accent="text-secondary-700" />
            <StatBox label="In QA" value={inQa} accent="text-amber-600" />
            <StatBox label="Blocked" value={blocked} accent="text-red-600" />
            <StatBox label="Overdue" value={overdueCount} accent="text-red-600" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-4">
              <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Completion</div>
              <div className="text-2xl font-bold">{done}<span className="text-secondary-400 font-normal">/{total}</span></div>
              <div className="text-xs text-secondary-500 mt-1">tasks finished</div>
            </div>
            <div className="card p-4">
              <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Team load</div>
              <div className="text-2xl font-bold">{teamLoad}%</div>
              <div className="text-xs text-secondary-500 mt-1">in-progress tasks per member</div>
            </div>
            <div className="card p-4">
              <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide mb-1">Time logged</div>
              <div className="text-2xl font-bold">{actualHours != null ? `${actualHours}h` : '—'}</div>
              <div className="text-xs text-secondary-500 mt-1">of {estHours}h estimated</div>
            </div>
          </div>

          {byStatus.length > 0 && (
            <div className="card p-5 mt-5">
              <div className="text-sm font-medium text-secondary-700 mb-3">Tasks by status</div>
              <div className="space-y-2">
                {STATUS_ORDER.filter((s) => byStatus.some((b) => b.status === s)).concat(
                  byStatus.filter((b) => !STATUS_ORDER.includes(b.status)).map((b) => b.status)
                ).map((statusName) => {
                  const item = byStatus.find((b) => b.status === statusName);
                  if (!item) return null;
                  const w = total > 0 ? (item.count / total) * 100 : 0;
                  return (
                    <div key={statusName} className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-16 sm:w-24 text-[11px] sm:text-xs text-secondary-600 flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                        <span className={`w-2 h-2 rounded-full ${statusDot[statusName] || 'bg-secondary-400'}`} />
                        {statusName}
                      </div>
                      <div className="flex-1 bg-secondary-100 rounded-full h-2 overflow-hidden">
                        <div className={`h-2 rounded-full ${statusDot[statusName]?.replace('bg-', 'bg-') || 'bg-secondary-400'}`} style={{ width: `${w}%` }} />
                      </div>
                      <span className="text-xs font-medium text-secondary-700 w-8 text-right tabular-nums">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* ── Tasks ── */}
        <section id="tasks" className="scroll-mt-24 sm:scroll-mt-28">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-5">
            <SectionTitle title="Tasks" count={allTasks.length} className="mb-0" />
            <div className="flex rounded-lg border border-secondary-200 ps-surface p-0.5 self-start">
              <button
                type="button"
                onClick={() => setTaskView('board')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${taskView === 'board' ? 'bg-primary-600 text-white' : 'text-secondary-600 hover:text-secondary-900'}`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Board
              </button>
              <button
                type="button"
                onClick={() => setTaskView('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${taskView === 'list' ? 'bg-primary-600 text-white' : 'text-secondary-600 hover:text-secondary-900'}`}
              >
                <List className="h-3.5 w-3.5" /> List
              </button>
            </div>
          </div>

          {!boardData ? (
            <LoadingBlock />
          ) : allTasks.length === 0 ? (
            <div className="card text-center py-12 text-secondary-500">No tasks yet.</div>
          ) : taskView === 'board' ? (
            <div className="public-share-kanban-wrap -mx-3 sm:mx-0 px-3 sm:px-0">
              <div
                className="public-share-kanban grid gap-3 sm:gap-4 w-full"
                style={{ '--kanban-cols': Math.max(columns.length, 1) }}
              >
                {columns.map((col) => (
                  <KanbanColumn key={col.status} status={col.status} tasks={col.tasks || []} />
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {allTasks.map((task) => (
                  <TaskCard key={task.id} task={task} status={task.status || 'To Do'} />
                ))}
              </div>
              <div className="card overflow-hidden p-0 hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-secondary-200 ps-muted text-left">
                      <th className="px-4 py-3 font-medium text-secondary-600">Task</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Status</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Priority</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Due</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Est.</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Sprint</th>
                      <th className="px-4 py-3 font-medium text-secondary-600">Assignees</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {allTasks.map((task) => (
                      <TaskRow key={task.id} task={task} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}
        </section>

        {/* ── Sprints ── */}
        <section id="sprints" className="scroll-mt-24 sm:scroll-mt-28">
          <SectionTitle title="Sprints" count={sprints.length} />
          {sprints.length === 0 ? (
            <div className="card text-center py-10 text-secondary-500 text-sm">No sprints defined.</div>
          ) : (
            <div className="space-y-4">
              {sprints.map((sprint) => (
                <div key={sprint.id} className="card p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                    <div>
                      <h3 className="font-semibold text-secondary-900">{sprint.name}</h3>
                      <p className="text-xs text-secondary-500 mt-0.5">
                        {sprint.start_date && sprint.end_date && (
                          <>
                            {formatDue(sprint.start_date)} – {formatDue(sprint.end_date)}
                          </>
                        )}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 self-start">
                      {sprint.tasks_count} task{sprint.tasks_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {sprint.goal && (
                    <p className="text-sm text-secondary-600 leading-relaxed border-t border-secondary-100 pt-3">{sprint.goal}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Team ── */}
        <section id="team" className="scroll-mt-24 sm:scroll-mt-28">
          <SectionTitle title="Team" count={members.length} />
          {!teamData ? (
            <LoadingBlock />
          ) : members.length === 0 ? (
            <div className="card text-center py-10 text-secondary-500 text-sm">No team members.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {members.map((m) => (
                <TeamCard key={m.id} member={m} />
              ))}
            </div>
          )}
        </section>

        {/* ── Time ── */}
        <section id="time" className="scroll-mt-24 sm:scroll-mt-28">
          <SectionTitle title="Time tracking" />
          {!analyticsData ? (
            <LoadingBlock />
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card p-5">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Estimated</div>
                  <div className="text-3xl font-bold mt-1 tabular-nums">{estHours}h</div>
                </div>
                <div className="card p-5">
                  <div className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Logged</div>
                  <div className="text-3xl font-bold mt-1 tabular-nums">{actualHours != null ? `${actualHours}h` : '—'}</div>
                  {estHours > 0 && actualHours != null && (
                    <div className="mt-3 w-full bg-secondary-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${actualHours > estHours ? 'bg-red-500' : 'bg-primary-500'}`}
                        style={{ width: `${Math.min((actualHours / estHours) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {barData && (
                <div className="card p-5">
                  <div className="text-sm font-medium text-secondary-700 mb-4">Hours per week (last 8 weeks)</div>
                  <div className="h-44 sm:h-52">
                    <Bar
                      data={barData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          x: { grid: { display: false } },
                          y: { beginAtZero: true, ticks: { stepSize: 1 } },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              {hoursByMember.length > 0 && (
                <div className="card overflow-hidden p-0">
                  <div className="px-5 py-3 border-b border-secondary-200 ps-muted">
                    <div className="text-sm font-medium text-secondary-700">Hours by team member</div>
                  </div>
                  <div className="divide-y divide-secondary-100">
                    {hoursByMember.map((row, i) => (
                      <div key={i} className="flex items-center justify-between px-5 py-3 text-sm">
                        <span className="text-secondary-800">{row.name}</span>
                        <span className="font-semibold tabular-nums text-secondary-900">{row.hours}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Activity ── */}
        <section id="activity" className="scroll-mt-24 sm:scroll-mt-28">
          <SectionTitle title="Recent activity" count={activity.length} />
          {activity.length === 0 ? (
            <div className="card text-center py-10 text-secondary-500 text-sm">No recent activity.</div>
          ) : (
            <div className="card divide-y divide-secondary-100 p-0 overflow-hidden">
              {activity.map((item, i) => (
                <div key={i} className="flex items-start gap-3 px-3 sm:px-5 py-3 sm:py-3.5">
                  <Activity className="h-4 w-4 text-secondary-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-secondary-800">{item.message}</p>
                    {item.timestamp && (
                      <p className="text-xs text-secondary-400 mt-0.5">{toUTCPlus5(item.timestamp)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-secondary-200 ps-surface py-4 mt-4">
        <div className="w-full min-w-0 px-4 md:px-6 lg:px-8 text-center">
          <span className="text-xs text-secondary-400">Shared project report</span>
        </div>
      </footer>
    </div>
  );
}

/* ─── Sub-components ─── */

function SectionTitle({ title, count, className = '' }) {
  return (
    <div className={`mb-5 ${className}`}>
      <h2 className="text-sm sm:text-base font-semibold text-secondary-900">
        {title}
        {count != null && <span className="ml-2 text-sm font-normal text-secondary-400">({count})</span>}
      </h2>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg ps-muted border border-secondary-100 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-xs text-secondary-500 mb-0.5">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-sm font-medium text-secondary-800 break-words leading-snug">{value}</div>
    </div>
  );
}

function StatBox({ label, value, accent = 'text-secondary-900' }) {
  return (
    <div className="card p-2.5 sm:p-3 text-center">
      <div className={`text-xl sm:text-2xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[10px] sm:text-xs text-secondary-500 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
    </div>
  );
}

function KanbanColumn({ status, tasks }) {
  return (
    <div className="min-w-0 w-full ps-column rounded-xl border border-secondary-200 p-2.5 sm:p-3 min-h-[240px] sm:min-h-[320px] public-share-kanban-col">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${statusDot[status] || 'bg-secondary-400'}`} />
          <span className="text-sm font-semibold text-secondary-800">{status}</span>
        </div>
        <span className="text-xs font-medium ps-surface text-secondary-600 px-2 py-0.5 rounded-md border border-secondary-200 tabular-nums">
          {tasks.length}
        </span>
      </div>
      <div className="space-y-2.5 max-h-[600px] overflow-y-auto">
        {tasks.map((task) => (
          <TaskCard key={task.id} task={task} status={status} />
        ))}
        {tasks.length === 0 && (
          <p className="text-xs text-secondary-400 text-center py-8">No tasks</p>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, status }) {
  const pri = task.priority_level?.toLowerCase();
  const overdue = isOverdue(task.due_date, status);
  return (
    <div className="ps-surface rounded-lg border border-secondary-200 p-3 shadow-sm">
      <div className="flex items-start gap-2">
        {task.is_subtask && (
          <span className="text-[10px] font-medium text-secondary-400 bg-secondary-100 px-1 py-0.5 rounded flex-shrink-0 mt-0.5">Sub</span>
        )}
        <h4 className="text-sm font-medium text-secondary-900 leading-snug">{task.name}</h4>
      </div>
      {task.description && (
        <p className="text-xs text-secondary-500 mt-1.5 leading-relaxed line-clamp-3">{task.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        {pri && priorityBadge[pri] && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${priorityBadge[pri]}`}>{pri}</span>
        )}
        {task.sprint_name && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 flex items-center gap-0.5">
            <Flag className="h-2.5 w-2.5" />{task.sprint_name}
          </span>
        )}
        {task.estimated_hours != null && (
          <span className="text-[10px] text-secondary-500 flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />{task.estimated_hours}h
          </span>
        )}
        {task.due_date && (
          <span className={`text-[10px] flex items-center gap-0.5 ${overdue ? 'text-red-600 font-medium' : 'text-secondary-500'}`}>
            {overdue ? <AlertTriangle className="h-2.5 w-2.5" /> : <Calendar className="h-2.5 w-2.5" />}
            {formatDue(task.due_date)}
          </span>
        )}
      </div>
      {task.assigned_names?.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-secondary-100">
          <div className="flex -space-x-1">
            {task.assigned_names.slice(0, 3).map((n, i) => (
              <div key={i} className="w-5 h-5 rounded-full bg-primary-100 border border-white flex items-center justify-center" title={n}>
                <span className="text-[8px] font-bold text-primary-700">{getInitials(n)}</span>
              </div>
            ))}
          </div>
          <span className="text-[10px] text-secondary-400 truncate">
            {task.assigned_names.length === 1 ? task.assigned_names[0] : `${task.assigned_names.length} assigned`}
          </span>
        </div>
      )}
    </div>
  );
}

function TaskRow({ task }) {
  const status = task.status || 'To Do';
  const pri = task.priority_level?.toLowerCase();
  const overdue = isOverdue(task.due_date, status);
  return (
    <tr className="hover:bg-secondary-50/50">
      <td className="px-4 py-3">
        <div className="font-medium text-secondary-900">{task.name}</div>
        {task.description && <div className="text-xs text-secondary-500 mt-0.5 line-clamp-1">{task.description}</div>}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-xs">
          <span className={`w-1.5 h-1.5 rounded-full ${statusDot[status] || 'bg-secondary-400'}`} />
          {status}
        </span>
      </td>
      <td className="px-4 py-3">
        {pri && priorityBadge[pri] ? (
          <span className={`text-xs px-1.5 py-0.5 rounded border capitalize ${priorityBadge[pri]}`}>{pri}</span>
        ) : '—'}
      </td>
      <td className={`px-4 py-3 text-xs whitespace-nowrap ${overdue ? 'text-red-600 font-medium' : 'text-secondary-600'}`}>
        {formatDue(task.due_date) || '—'}
      </td>
      <td className="px-4 py-3 text-xs text-secondary-600 tabular-nums">
        {task.estimated_hours != null ? `${task.estimated_hours}h` : '—'}
      </td>
      <td className="px-4 py-3 text-xs text-secondary-600">{task.sprint_name || '—'}</td>
      <td className="px-4 py-3 text-xs text-secondary-600">
        {task.assigned_names?.length ? task.assigned_names.join(', ') : '—'}
      </td>
    </tr>
  );
}

function TeamCard({ member }) {
  const assigned = member.tasks_assigned || 0;
  const completed = member.tasks_completed || 0;
  const memberPct = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
  const current = member.current_tasks || [];
  const displayName = member.full_name || member.first_name;
  return (
    <div className="card p-4 h-full">
      <div className="flex h-full flex-col">
        <div className="relative mb-4 flex-shrink-0 text-center">
          <span className="absolute top-0 right-0 text-lg font-bold tabular-nums text-secondary-900">
            {memberPct}%
          </span>
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
            <span className="text-sm font-bold text-primary-700">{getInitials(displayName)}</span>
          </div>
          <h4 className="font-semibold text-sm text-secondary-900 leading-snug px-8">{displayName}</h4>
          <p className="text-xs text-secondary-500 mt-0.5">{roleLabels[member.role] || 'Member'}</p>
        </div>

        <div className="w-full bg-secondary-200 rounded-full h-1.5 mb-3 flex-shrink-0 overflow-hidden">
          <div
            className={`h-1.5 rounded-full ${memberPct === 100 ? 'bg-emerald-500' : 'bg-primary-500'}`}
            style={{ width: `${memberPct}%` }}
          />
        </div>

        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-secondary-600 mb-3 flex-shrink-0">
          <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{assigned} assigned</span>
          <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" />{completed} done</span>
          {(member.tasks_in_progress ?? 0) > 0 && (
            <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-primary-500" />{member.tasks_in_progress} active</span>
          )}
        </div>

        <div className="border-t border-secondary-100 pt-3 flex-1 min-h-[5rem]">
          <p className="text-[11px] font-medium text-secondary-400 uppercase tracking-wide mb-2">Open tasks</p>
          {current.length > 0 ? (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {current.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot[t.status] || 'bg-secondary-400'}`} />
                  <span className="text-secondary-700 truncate">{t.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-secondary-400">No open tasks</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordForm({ title, subtitle, subtitleColor, passwordInput, setPasswordInput, onSubmit, loading }) {
  return (
    <div className="card max-w-md w-full p-8">
      <h1 className="text-lg font-semibold text-secondary-900 mb-1">{title}</h1>
      {subtitle && <p className={`${subtitleColor || 'text-secondary-500'} text-sm mb-5`}>{subtitle}</p>}
      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="password"
          value={passwordInput}
          onChange={(e) => setPasswordInput(e.target.value)}
          placeholder="Enter password"
          className="input-field w-full"
          autoFocus
        />
        <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading}>
          <Eye className="h-4 w-4" />{loading ? 'Verifying…' : 'View project'}
        </button>
      </form>
    </div>
  );
}
